// eslint-disable-next-line max-classes-per-file
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { ToastContext } from '@seccon/core/contexts/toast';
import CSRFContext from '@seccon/core/contexts/csrf';

class UpdateError extends Error {}
class DocumentDeleteError extends Error {}
class DocumentUploadError extends Error {}
class NetworkError extends Error {
  constructor (message, documentNames) {
    super(message);
    this.documentNames = documentNames;
  }
}

function convertYesNoNAToBoolean (value) {
  return value === 'n/a' ? null : (value === 'yes');
}

const useUpdateReport = (foreignTravelId, origin, destinationPage, initialDocuments, cognizantOffice) => {
  const toastApi = React.useContext(ToastContext);
  const csrfToken = React.useContext(CSRFContext);

  async function handleUpdateRequest (formData) {
    try {
      const foreignTravel = {
        origin,
        foreignTravel: {
          ...formData.foreignTravel,
          departureDate: formData.foreignTravel.departureDate.format('MM/DD/YYYY'),
          returnDate: formData.foreignTravel.returnDate.format('MM/DD/YYYY'),
          willUseForeignPassport: convertYesNoNAToBoolean(formData.foreignTravel.willUseForeignPassport),
          travelingPersonnel: {
            ...formData.foreignTravel.travelingPersonnel,
            id: formData.foreignTravel.travelingPersonnel.id.id,
            fullName: formData.foreignTravel.travelingPersonnel.id.fullName,
            passport: {
              ...formData.foreignTravel.travelingPersonnel.passport,
              expirationDate: formData.foreignTravel.travelingPersonnel.passport.expirationDate.format('MM/DD/YYYY'),
              issueDate: formData.foreignTravel.travelingPersonnel.passport.issueDate.format('MM/DD/YYYY')
            }
          },
          destinations: formData.foreignTravel.destinations.map((destination) => ({
            ...destination,
            startDate: destination.startDate.format('MM/DD/YYYY'),
            endDate: destination.endDate.format('MM/DD/YYYY')
          }))
        }
      };

      // Documents should not be included in the update report request
      delete foreignTravel.documents;

      if (formData.incident) {
        foreignTravel.incident = {
          ...formData.incident,
          cognizantSecurityAuthority: cognizantOffice.find((entry) => entry.office === formData.incident.cognizantSecurityAuthority),
          compromise: convertYesNoNAToBoolean(formData.incident.compromise),
          cognizantOfficeNotified: convertYesNoNAToBoolean(formData.incident.cognizantOfficeNotified),
          incidentDate: formData.incident.incidentDate ? formData.incident.incidentDate.format('MM/DD/YYYY') : null,
          initialReportDate: formData.incident.initialReportDate ? formData.incident.initialReportDate.format('MM/DD/YYYY') : null,
          finalReportDate: formData.incident.finalReportDate ? formData.incident.finalReportDate.format('MM/DD/YYYY') : null
        };
      }

      const response = await fetch(`/foreignTravel/${ foreignTravelId }`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'anti-csrf-token': csrfToken
        },
        body: JSON.stringify(foreignTravel)
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${ response.status }`);
      }
    } catch (error) {
      console.error('Error creating report', error);

      throw new UpdateError('Unable to update foreign travel report');
    }
  }

  async function handleRemoveDocuments (formDataDocuments) {
    try {
      const formDataDocNames = new Set(formDataDocuments.map((formDoc) => formDoc.name));
      const removedDocs = initialDocuments.filter((initDoc) => !formDataDocNames.has(initDoc.filename));

      let failedResponses = await Promise.all(
        removedDocs.map((removedDoc) => (
          fetch(`/employee/deleteIncidentDocument/${ removedDoc.id }`, {
            method: 'DELETE',
            headers: {
              'anti-csrf-token': csrfToken
            }
          }).then((resp) => (!resp.ok ? removedDoc.filename : null))
        ))
      );

      failedResponses = failedResponses.filter((resp) => !!resp);

      if (failedResponses.length > 0) {
        throw new NetworkError('Network response was not ok', failedResponses);
      }
    } catch (error) {
      console.error('Error removing documents:', error);

      if (error instanceof NetworkError) {
        throw new DocumentDeleteError(`Report was updated, however some documents could not be removed (${ error.documentNames.join(', ') }). Update those reports and try again.`);
      }

      throw new DocumentDeleteError('Report was updated, however some documents could not be removed. Reload the page and try again.');
    }
  }

  async function handleUploadDocuments (formDataDocuments) {
    try {
      const addedDocs = formDataDocuments.filter((file) => file.type !== 'application/custom');

      if (addedDocs.length === 0) {
        console.log('No new documents provided, skipping upload.');

        return;
      }

      const formData = new FormData();
      addedDocs.forEach((file) => {
        formData.append('foreignTravelDocument', file);
      });

      const response = await fetch(`/foreignTravel/${ foreignTravelId }/upload?origin=${ origin }`, {
        method: 'POST',
        headers: {
          'anti-csrf-token': csrfToken
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${ response.status }`);
      }
    } catch (error) {
      console.error('Error uploading documents:', error);

      throw new DocumentUploadError('Report was updated, however some documents could not be added. Reload the page and try again.');
    }
  }

  const handleUpdateSuccess = () => {
    toastApi.success('Successfully updated foreign travel report');
    setTimeout(() => {
      window.location = destinationPage;
    }, 3000);
  };

  const handleUpdateError = (error) => {
    if (error instanceof UpdateError) {
      toastApi.error(error.message);
    } else if (error instanceof DocumentDeleteError || error instanceof DocumentUploadError) {
      toastApi.warning(error.message);
    } else {
      toastApi.error('Unable to update foreign travel report');
    }
  };

  const { mutateAsync: updateReport, isPending: isSubmitting } = useMutation({
    mutationFn: async (formData) => {
      await handleUpdateRequest(formData);

      console.log('--Foreign Travel is Updated');

      await handleRemoveDocuments(formData.foreignTravel.documents);

      console.log('--Documents are removed');

      await handleUploadDocuments(formData.foreignTravel.documents);
    },
    onSuccess: handleUpdateSuccess,
    onError: handleUpdateError
  });

  return { updateReport, isSubmitting };
};

export default useUpdateReport;
